import React, { useEffect } from 'react';
import { CheckCircle2, XCircle, Clock, ShieldCheck, BookOpen, Activity, Brain, AlertCircle, ArrowRight, Battery, Droplets, Heart, Sparkles, Scale } from 'lucide-react';
import { CTAButton } from '../components/CTAButton';
import { Accordion } from '../components/Accordion';
import { FadeIn } from '../components/FadeIn';
import { Footer } from '../components/Footer';
import { Navbar } from '../components/Navbar';
import { initPixel, trackPageView } from '../utils/metaPixel';
export function LandingPage() {
  useEffect(() => {
    initPixel();
    trackPageView();
  }, []);
  return <div className="min-h-screen bg-cream-50 font-sans text-sage-900 selection:bg-sage-200" data-id="element-90">
      <Navbar data-id="element-91" />

      {/* 1. HERO SECTION */}
      <section className="relative pt-28 pb-16 md:pt-40 md:pb-24 overflow-hidden" data-id="element-92">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(ellipse_at_top_right,_var(--tw-gradient-stops))] from-sage-100/50 via-cream-50 to-cream-50 -z-10" data-id="element-93"></div>

        <div className="container mx-auto px-6 max-w-6xl" data-id="element-94">
          <div className="flex flex-col md:flex-row items-center gap-12 md:gap-20" data-id="element-95">
            <FadeIn className="flex-1 text-center md:text-left" data-id="element-96">
              <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-sage-100 text-sage-700 text-base font-semibold mb-6" data-id="element-97">
                <Sparkles className="w-5 h-5" data-id="element-98" />
                <span data-id="element-99">Especial para mujeres 55+</span>
              </div>
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-serif font-bold text-sage-900 leading-[1.1] mb-8" data-id="element-100">
                Deshincha tu abdomen en 7 días{' '}
                <span className="text-sage-600 italic" data-id="element-101">
                  sin ejercicios de impacto
                </span>
              </h1>
              <p className="text-xl md:text-2xl text-sage-700 mb-10 leading-relaxed max-w-2xl mx-auto md:mx-0" data-id="element-102">
                El Protocolo de Cortisol Bajo para mujeres de 55+ que ya
                intentaron todo. Recupera tu cintura y tu energía apagando la
                inflamación desde la raíz.
              </p>

              <div className="flex flex-col items-center md:items-start gap-4" data-id="element-103">
                <CTAButton scrollTo="pricing" data-id="element-104">Empezar Mi Plan Ahora</CTAButton>
                <div className="flex flex-wrap items-center justify-center md:justify-start gap-4 text-base text-sage-600 font-semibold mt-3" data-id="element-105">
                  <span className="flex items-center gap-1.5" data-id="element-106">
                    <Clock className="w-5 h-5" data-id="element-107" /> Acceso inmediato
                  </span>
                  <span className="hidden sm:inline" data-id="element-108">•</span>
                  <span className="flex items-center gap-1.5" data-id="element-109">
                    <BookOpen className="w-5 h-5" data-id="element-110" /> Descarga digital
                  </span>
                  <span className="hidden sm:inline" data-id="element-111">•</span>
                  <span className="flex items-center gap-1.5" data-id="element-112">
                    <ShieldCheck className="w-5 h-5" data-id="element-113" /> Garantía de 7 días
                  </span>
                </div>
              </div>
            </FadeIn>

            <FadeIn delay={0.2} className="flex-1 w-full max-w-md md:max-w-none relative" data-id="element-114">
              <div className="absolute inset-0 bg-sage-200 rounded-full blur-3xl opacity-30 animate-pulse" data-id="element-115"></div>
              <img src="https://cdn.magicpatterns.com/uploads/7qQEe54DjePPssTFSJ9K4W/heylloyd-image-1776030781686.png" alt="Método Vientre Plano 55+ Ebook en Tablet" className="relative z-10 w-full h-auto drop-shadow-2xl rounded-xl" data-id="element-116" />
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 2. PAIN POINTS SECTION */}
      <section id="method" className="py-20 bg-white" data-id="element-117">
        <div className="container mx-auto px-6 max-w-5xl" data-id="element-118">
          <FadeIn className="text-center mb-16" data-id="element-119">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-sage-900 mb-5" data-id="element-120">
              ¿Te identificas con esto?
            </h2>
            <p className="text-xl text-sage-600 max-w-2xl mx-auto" data-id="element-121">
              No estás sola, y lo más importante: no es tu culpa ni falta de
              voluntad.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-122">
            {[{
            icon: Droplets,
            text: 'Te despiertas plana pero al mediodía ya estás hinchada como un globo.'
          }, {
            icon: Activity,
            text: 'Caminas millas, sudas horas, y sigues con el mismo abdomen inflamado.'
          }, {
            icon: AlertCircle,
            text: "Tu pantalón favorito ya no cierra aunque comes 'sano' todos los días."
          }, {
            icon: Brain,
            text: 'Sientes niebla mental, cansancio constante y falta de energía vital.'
          }, {
            icon: Scale,
            text: 'Has probado dietas de 1,200 calorías que solo arruinaron tu metabolismo.'
          }].map((item, i) => <FadeIn key={i} delay={i * 0.1} className="bg-cream-50 p-8 rounded-2xl border border-sage-100 hover:border-sage-300 transition-colors" data-id="element-123">
                <div className="w-14 h-14 bg-sage-100 rounded-full flex items-center justify-center text-sage-600 mb-5" data-id="element-124">
                  <item.icon className="w-7 h-7" data-id="element-125" />
                </div>
                <p className="text-lg text-sage-800 font-semibold leading-relaxed" data-id="element-126">
                  {item.text}
                </p>
              </FadeIn>)}
          </div>
        </div>
      </section>

      {/* 3. PROBLEM EXPLANATION */}
      <section className="py-20 bg-sage-900 text-cream-50" data-id="element-127">
        <div className="container mx-auto px-6 max-w-5xl" data-id="element-128">
          <FadeIn className="text-center mb-16" data-id="element-129">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-cream-50 mb-5" data-id="element-130">
              Lo que nadie te ha dicho sobre tu cuerpo a los 55+
            </h2>
            <p className="text-xl text-sage-200 max-w-3xl mx-auto" data-id="element-131">
              Después de la menopausia, las reglas cambian. Lo que funcionaba a
              los 30, ahora es gasolina para el incendio de la inflamación.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-132">
            <FadeIn delay={0.1} className="bg-sage-800 p-8 rounded-2xl border border-sage-700" data-id="element-133">
              <h3 className="text-2xl font-serif font-bold text-amber-400 mb-4" data-id="element-134">
                El Interruptor del Cortisol
              </h3>
              <p className="text-lg text-sage-100 leading-relaxed" data-id="element-135">
                A los 55+, tu cuerpo es hipersensible al estrés. El cortisol
                alto le dice a tu cuerpo: "guarda energía en el abdomen por
                seguridad". No es grasa, es inflamación por alerta constante.
              </p>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-sage-800 p-8 rounded-2xl border border-sage-700" data-id="element-136">
              <h3 className="text-2xl font-serif font-bold text-amber-400 mb-4" data-id="element-137">
                La Mentira del Cardio
              </h3>
              <p className="text-lg text-sage-100 leading-relaxed" data-id="element-138">
                Nos enseñaron a "quemar" más. Pero el ejercicio intenso a esta
                edad eleva el cortisol basal. Tu cuerpo percibe el esfuerzo como
                peligro y retiene líquidos para protegerse.
              </p>
            </FadeIn>

            <FadeIn delay={0.3} className="bg-sage-800 p-8 rounded-2xl border border-sage-700" data-id="element-139">
              <h3 className="text-2xl font-serif font-bold text-amber-400 mb-4" data-id="element-140">
                El Mito de la Fibra Cruda
              </h3>
              <p className="text-lg text-sage-100 leading-relaxed" data-id="element-141">
                Comer ensaladas frías de noche gasta demasiada energía térmica.
                Tu digestión se vuelve lenta, la comida fermenta y te despiertas
                inflamada. Necesitas comida tibia y fácil de romper.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 4. WHAT'S INSIDE */}
      <section id="content" className="py-20 bg-cream-50" data-id="element-142">
        <div className="container mx-auto px-6 max-w-5xl" data-id="element-143">
          <FadeIn className="text-center mb-16" data-id="element-144">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-sage-900 mb-5" data-id="element-145">
              Qué incluye tu guía paso a paso
            </h2>
            <p className="text-xl text-sage-600" data-id="element-146">
              Un manual claro, compasivo y directo para recuperar tu bienestar.
            </p>
          </FadeIn>

          <div className="bg-white rounded-3xl shadow-sm border border-sage-100 p-8 md:p-12" data-id="element-147">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-148">
              <div data-id="element-149">
                <h3 className="text-2xl font-serif font-bold text-sage-900 mb-6 border-b border-sage-100 pb-4" data-id="element-150">
                  El Método Principal
                </h3>
                <ul className="space-y-6" data-id="element-151">
                  {[{
                  title: 'Cap 1: El Engaño Metabólico',
                  desc: 'Por qué tu abdomen no es grasa, es inflamación.'
                }, {
                  title: 'Cap 2: Desactivando el Estrés',
                  desc: 'Técnicas de apagado de emergencia para tu sistema nervioso.'
                }, {
                  title: 'Cap 3: Protocolo de 7 Días',
                  desc: 'Qué comer cada mañana, paso a paso, sin pasar hambre.'
                }, {
                  title: 'Cap 4: Movimientos Linfáticos',
                  desc: 'Rutina de 5 minutos en cama (no abdominales).'
                }, {
                  title: 'Cap 5: Mantenimiento de por vida',
                  desc: 'La regla 80/20 para no volver a hincharte jamás.'
                }].map((item, i) => <li key={i} className="flex gap-4" data-id="element-152">
                      <div className="mt-1 bg-sage-100 text-sage-600 rounded-full p-1.5 h-fit" data-id="element-153">
                        <CheckCircle2 className="w-6 h-6" data-id="element-154" />
                      </div>
                      <div data-id="element-155">
                        <h4 className="text-lg font-bold text-sage-900" data-id="element-156">
                          {item.title}
                        </h4>
                        <p className="text-base text-sage-600 mt-1" data-id="element-157">
                          {item.desc}
                        </p>
                      </div>
                    </li>)}
                </ul>
              </div>

              <div className="bg-sage-50 rounded-2xl p-8 border border-sage-200" data-id="element-158">
                <h3 className="text-2xl font-serif font-bold text-sage-900 mb-6 flex items-center gap-2" data-id="element-159">
                  <Sparkles className="w-6 h-6 text-amber-500" data-id="element-160" />
                  Bonos Exclusivos Incluidos
                </h3>
                <ul className="space-y-5" data-id="element-161">
                  <li className="bg-white p-5 rounded-xl shadow-sm border border-sage-100" data-id="element-162">
                    <h4 className="font-bold text-sage-800 text-base" data-id="element-163">
                      🛒 Lista de Compras "Sin Hinchazón"
                    </h4>
                    <p className="text-sm text-sage-600 mt-1" data-id="element-164">
                      Exactamente qué comprar en supermercados de USA.
                    </p>
                  </li>
                  <li className="bg-white p-5 rounded-xl shadow-sm border border-sage-100" data-id="element-165">
                    <h4 className="font-bold text-sage-800 text-base" data-id="element-166">
                      📝 Rastreador Diario de Inflamación
                    </h4>
                    <p className="text-sm text-sage-600 mt-1" data-id="element-167">
                      Mide tu progreso real mañana vs. noche.
                    </p>
                  </li>
                  <li className="bg-white p-5 rounded-xl shadow-sm border border-sage-100" data-id="element-168">
                    <h4 className="font-bold text-sage-800 text-base" data-id="element-169">
                      🥂 Guía: Alcohol sin Inflamación
                    </h4>
                    <p className="text-sm text-sage-600 mt-1" data-id="element-170">
                      Qué beber en salidas sociales sin arruinar tu progreso.
                    </p>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 5. BENEFITS / RESULTS */}
      <section id="results" className="py-20 bg-white" data-id="element-171">
        <div className="container mx-auto px-6 max-w-4xl" data-id="element-172">
          <FadeIn className="text-center mb-16" data-id="element-173">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-sage-900 mb-5" data-id="element-174">
              La transformación que experimentarás
            </h2>
            <p className="text-xl text-sage-600" data-id="element-175">
              No es magia, es biología aplicada a tu etapa de vida.
            </p>
          </FadeIn>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-176">
            <FadeIn delay={0.1} className="bg-cream-50 p-8 rounded-2xl border border-red-100 relative overflow-hidden" data-id="element-177">
              <div className="absolute top-0 left-0 w-2 h-full bg-red-300" data-id="element-178"></div>
              <h3 className="text-2xl font-bold text-sage-900 mb-5 flex items-center gap-2" data-id="element-179">
                <span className="text-red-400" data-id="element-180">Día 1:</span> El Punto de Partida
              </h3>
              <ul className="space-y-4 text-sage-700" data-id="element-181">
                <li className="flex items-start gap-3 text-lg" data-id="element-182">
                  <XCircle className="w-6 h-6 text-red-300 shrink-0 mt-0.5" data-id="element-183" />{' '}
                  Sensación de pesadez constante
                </li>
                <li className="flex items-start gap-3 text-lg" data-id="element-184">
                  <XCircle className="w-6 h-6 text-red-300 shrink-0 mt-0.5" data-id="element-185" />{' '}
                  Ropa ajustada e incómoda
                </li>
                <li className="flex items-start gap-3 text-lg" data-id="element-186">
                  <XCircle className="w-6 h-6 text-red-300 shrink-0 mt-0.5" data-id="element-187" />{' '}
                  Energía por los suelos a media tarde
                </li>
                <li className="flex items-start gap-3 text-lg" data-id="element-188">
                  <XCircle className="w-6 h-6 text-red-300 shrink-0 mt-0.5" data-id="element-189" />{' '}
                  Frustración frente al espejo
                </li>
              </ul>
            </FadeIn>

            <FadeIn delay={0.2} className="bg-sage-50 p-8 rounded-2xl border border-sage-200 relative overflow-hidden shadow-md" data-id="element-190">
              <div className="absolute top-0 left-0 w-2 h-full bg-sage-500" data-id="element-191"></div>
              <h3 className="text-2xl font-bold text-sage-900 mb-5 flex items-center gap-2" data-id="element-192">
                <span className="text-sage-600" data-id="element-193">Día 7:</span> El Renacer
              </h3>
              <ul className="space-y-4 text-sage-800 font-semibold" data-id="element-194">
                <li className="flex items-start gap-3 text-lg" data-id="element-195">
                  <CheckCircle2 className="w-6 h-6 text-sage-500 shrink-0 mt-0.5" data-id="element-196" />{' '}
                  Abdomen visiblemente desinflamado
                </li>
                <li className="flex items-start gap-3 text-lg" data-id="element-197">
                  <CheckCircle2 className="w-6 h-6 text-sage-500 shrink-0 mt-0.5" data-id="element-198" />{' '}
                  Ese pantalón vuelve a cerrar sin esfuerzo
                </li>
                <li className="flex items-start gap-3 text-lg" data-id="element-199">
                  <CheckCircle2 className="w-6 h-6 text-sage-500 shrink-0 mt-0.5" data-id="element-200" />{' '}
                  Claridad mental y energía estable
                </li>
                <li className="flex items-start gap-3 text-lg" data-id="element-201">
                  <CheckCircle2 className="w-6 h-6 text-sage-500 shrink-0 mt-0.5" data-id="element-202" />{' '}
                  Confianza renovada en tu propio cuerpo
                </li>
              </ul>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 6. TARGET AUDIENCE */}
      <section className="py-20 bg-cream-100" data-id="element-203">
        <div className="container mx-auto px-6 max-w-5xl" data-id="element-204">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-205">
            <FadeIn data-id="element-206">
              <h3 className="text-3xl font-serif font-bold text-sage-900 mb-8 flex items-center gap-3" data-id="element-207">
                <Heart className="w-7 h-7 text-sage-600" data-id="element-208" />
                Este método ES para ti si:
              </h3>
              <ul className="space-y-5" data-id="element-209">
                {['Eres una mujer de 55 a 65 años viviendo en USA.', "Sientes que tu cuerpo cambió 'de la noche a la mañana'.", 'Estás cansada de dietas restrictivas que te dejan con hambre.', 'Buscas una solución natural basada en hábitos suaves.', 'Quieres entender a tu cuerpo en lugar de pelear con él.'].map((text, i) => <li key={i} className="flex items-start gap-3" data-id="element-210">
                    <CheckCircle2 className="w-7 h-7 text-sage-600 shrink-0" data-id="element-211" />
                    <span className="text-lg text-sage-800 font-medium" data-id="element-212">
                      {text}
                    </span>
                  </li>)}
              </ul>
            </FadeIn>

            <FadeIn delay={0.2} data-id="element-213">
              <h3 className="text-3xl font-serif font-bold text-sage-900 mb-8 flex items-center gap-3" data-id="element-214">
                <AlertCircle className="w-7 h-7 text-red-400" data-id="element-215" />
                Este método NO es para ti si:
              </h3>
              <ul className="space-y-5" data-id="element-216">
                {['Buscas pastillas mágicas o soluciones de un día.', 'Quieres rutinas de ejercicio extremo y sudoroso.', 'No estás dispuesta a hacer pequeños cambios en tu cena.', 'Prefieres contar calorías obsesivamente.', 'Buscas intervenciones quirúrgicas o fajas apretadas.'].map((text, i) => <li key={i} className="flex items-start gap-3" data-id="element-217">
                    <XCircle className="w-7 h-7 text-red-400 shrink-0" data-id="element-218" />
                    <span className="text-lg text-sage-700" data-id="element-219">{text}</span>
                  </li>)}
              </ul>
            </FadeIn>
          </div>
        </div>
      </section>

      {/* 7. PRICING & CTA */}
      <section id="pricing" className="py-24 bg-white relative scroll-mt-8" data-id="element-220">
        <div className="absolute inset-0 bg-sage-900/5 skew-y-3 transform origin-top-left -z-10" data-id="element-221"></div>
        <div className="container mx-auto px-6 max-w-4xl" data-id="element-222">
          <FadeIn className="bg-white rounded-3xl shadow-2xl border border-sage-100 overflow-hidden max-w-2xl mx-auto" data-id="element-223">
            <div className="bg-sage-900 p-10 text-center text-cream-50" data-id="element-224">
              <h2 className="text-4xl font-serif font-bold mb-3" data-id="element-225">
                Comienza tu transformación hoy
              </h2>
              <p className="text-xl text-sage-300" data-id="element-226">
                Acceso inmediato a todo el material digital
              </p>
            </div>

            <div className="p-8 md:p-12 text-center" data-id="element-227">
              <div className="flex justify-center items-baseline gap-2 mb-8" data-id="element-228">
                <span className="text-6xl font-bold text-sage-900" data-id="element-229">$6.99</span>
                <span className="text-2xl text-sage-500 font-semibold" data-id="element-230">
                  USD
                </span>
              </div>

              <ul className="text-left space-y-5 mb-10 max-w-md mx-auto" data-id="element-231">
                <li className="flex items-center gap-3 text-lg text-sage-800 font-medium" data-id="element-232">
                  <CheckCircle2 className="w-6 h-6 text-sage-500 shrink-0" data-id="element-233" />{' '}
                  Ebook Completo: Método Vientre Plano 55+
                </li>
                <li className="flex items-center gap-3 text-lg text-sage-800 font-medium" data-id="element-234">
                  <CheckCircle2 className="w-6 h-6 text-sage-500 shrink-0" data-id="element-235" />{' '}
                  Protocolo de 7 Días Paso a Paso
                </li>
                <li className="flex items-center gap-3 text-lg text-sage-800 font-medium" data-id="element-236">
                  <CheckCircle2 className="w-6 h-6 text-sage-500 shrink-0" data-id="element-237" />{' '}
                  Bono: Lista de Compras USA
                </li>
                <li className="flex items-center gap-3 text-lg text-sage-800 font-medium" data-id="element-238">
                  <CheckCircle2 className="w-6 h-6 text-sage-500 shrink-0" data-id="element-239" />{' '}
                  Bono: Rastreador de Inflamación
                </li>
                <li className="flex items-center gap-3 text-lg text-sage-800 font-medium" data-id="element-240">
                  <CheckCircle2 className="w-6 h-6 text-sage-500 shrink-0" data-id="element-241" />{' '}
                  Bono: Guía Alcohol sin Inflamación
                </li>
              </ul>

              <CTAButton href="https://bienestarypaz.com//cart/46669473579164:1" className="w-full md:w-auto text-2xl px-14 py-6 shadow-sage-600/30" data-id="element-242">
                Comprar Mi Plan de 7 Días
              </CTAButton>

              <div className="mt-8 flex items-center justify-center gap-2 text-sage-600 bg-sage-50 py-4 px-6 rounded-lg" data-id="element-243">
                <ShieldCheck className="w-6 h-6" data-id="element-244" />
                <span className="text-base font-semibold" data-id="element-245">
                  Garantía de devolución de 7 días. Cero riesgos.
                </span>
              </div>
            </div>
          </FadeIn>
        </div>
      </section>

      {/* 8. FAQ */}
      <section id="faq" className="py-20 bg-cream-50" data-id="element-246">
        <div className="container mx-auto px-6" data-id="element-247">
          <FadeIn className="text-center mb-12" data-id="element-248">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-sage-900 mb-5" data-id="element-249">
              Preguntas Frecuentes
            </h2>
            <p className="text-xl text-sage-600" data-id="element-250">
              Resolvemos tus dudas antes de empezar.
            </p>
          </FadeIn>

          <FadeIn delay={0.2} data-id="element-251">
            <Accordion items={[{
            question: '¿Puedo tomar café?',
            answer: 'Sí, pero nunca con el estómago vacío. Toma tu agua con limón primero y desayuna. El café en ayunas dispara el cortisol digestivo y promueve la inflamación abdominal.'
          }, {
            question: '¿Por qué no puedo comer ensaladas de noche?',
            answer: 'Porque tu cuerpo requiere mucha energía térmica para digerir fibra cruda. De noche, tu temperatura baja y tu energía se va a la reparación celular, no a la digestión. La ensalada se queda ahí, fermentándose y causando gases.'
          }, {
            question: '¿Tengo que hacer esto para siempre?',
            answer: "No. Los 7 días son un 'reset' profundo para ver resultados rápidos y apagar el fuego interno. Después, te enseñamos la regla 80/20 para mantener los resultados mientras disfrutas de tu vida y comidas favoritas."
          }, {
            question: '¿Sirve si tomo medicamentos para la presión o tiroides?',
            answer: 'Sí, este método se basa en comida real y natural, no en suplementos extraños. Sin embargo, siempre recomendamos informar a tu médico sobre cualquier cambio en tus hábitos alimenticios.'
          }, {
            question: '¿Cuándo empezaré a ver resultados?',
            answer: 'La mayoría de nuestras lectoras notan una reducción significativa en la hinchazón y una mejora en su energía entre el día 3 y 4 del protocolo. Para el día 7, la diferencia en cómo te queda la ropa suele ser notable.'
          }]} data-id="element-252" />
          </FadeIn>
        </div>
      </section>

      {/* 9. FINAL CTA & FOOTER */}
      <section className="bg-sage-900 text-cream-50 pt-20 pb-10" data-id="element-253">
        <div className="container mx-auto px-6 text-center" data-id="element-254">
          <FadeIn className="max-w-3xl mx-auto mb-16" data-id="element-255">
            <h2 className="text-4xl md:text-5xl font-serif font-bold mb-8" data-id="element-256">
              Mereces sentirte cómoda en tu propia piel
            </h2>
            <p className="text-2xl text-sage-200 mb-12 leading-relaxed" data-id="element-257">
              Has pasado toda tu vida cuidando de otros. Es momento de escuchar
              a tu cuerpo, apagar la alerta constante y recuperar la ligereza
              que te pertenece. Este método funciona porque respeta quién eres
              hoy.
            </p>
            <CTAButton href="https://bienestarypaz.com//cart/46669473579164:1" className="bg-amber-500 hover:bg-amber-600 text-sage-900 shadow-amber-500/20 text-2xl px-14 py-6" data-id="element-258">
              Obtener Mi Guía Ahora
            </CTAButton>
          </FadeIn>

          <Footer data-id="element-259" />
        </div>
      </section>
    </div>;
}